import { useState, useEffect, useCallback } from "react";
import {
  listPermissions,
  type PermissionResponse,
} from "../api/accessManagement/permissionsService";

export interface PermissionOption {
  value: string;
  label: string;
}

interface UsePermissionOptionsResult {
  permissionOptions: PermissionOption[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function mapPermissionsToOptions(
  permissions: PermissionResponse[]
): PermissionOption[] {
  return permissions.map((permission) => {
    const code = permission.code ?? permission.permission ?? "";
    return {
      value: code,
      label: code,
    };
  });
}

/**
 * Fetches permission options for dropdowns, with optional server-side search.
 * The search parameter is expected to be debounced by the caller.
 */
export function usePermissionOptions(
  search: string = ""
): UsePermissionOptionsResult {
  const [permissionOptions, setPermissionOptions] = useState<PermissionOption[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissionOptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listPermissions({
        page: 1,
        limit: 50,
        search: search || undefined,
      });
      const options = mapPermissionsToOptions(data || []);
      setPermissionOptions(options);
    } catch (err) {
      console.error("Failed to fetch permissions for dropdown:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load permissions for dropdown"
      );
      setPermissionOptions([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchPermissionOptions();
  }, [fetchPermissionOptions]);

  const refetch = useCallback(() => {
    fetchPermissionOptions();
  }, [fetchPermissionOptions]);

  return { permissionOptions, loading, error, refetch };
}

